import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DOMPurify from 'dompurify';
import { 
  ArrowLeft, Heart, Eye, Clock, User, Flag, 
  MessageCircle, ChevronDown, ChevronUp, Loader2, 
  Bookmark, Trophy, PenLine, Send,
  BookOpen, ChevronLeft, ChevronRight, List
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import ShareMenu from '../components/ShareMenu';
import CommentSection from '../components/CommentSection';
import ExportMenu from '../components/ExportMenu';

const StoryReaderPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, api } = useAuth();
  const [story, setStory] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const [showComments, setShowComments] = useState(true);
  const [readingProgress, setReadingProgress] = useState(0);
  const [bookmarked, setBookmarked] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [currentChapter, setCurrentChapter] = useState(0);
  const [showToc, setShowToc] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize / sync currentChapter from ?chapter=N URL param (one-shot on mount)
  useEffect(() => {
    const raw = searchParams.get('chapter');
    if (raw !== null) {
      const n = parseInt(raw, 10);
      if (Number.isFinite(n) && n >= 0) setCurrentChapter(n);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist currentChapter to URL (?chapter=N) for deep-linking and reload-stability
  useEffect(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (currentChapter > 0) next.set('chapter', String(currentChapter));
      else next.delete('chapter');
      return next;
    }, { replace: true });
  }, [currentChapter, setSearchParams]);

  // Parse chapters from content
  const parseChapters = (content) => {
    if (!content) return [{ title: null, content: '' }];
    const parts = content.split(/\[chapter:(.*?)\]/);
    if (parts.length <= 1) return [{ title: null, content: content }];
    
    const chapters = [];
    // Text before first chapter marker
    const preamble = parts[0].trim();
    if (preamble) {
      chapters.push({ title: 'Prologue', content: preamble });
    }
    // Each chapter: parts[1]=title, parts[2]=content, parts[3]=title, parts[4]=content...
    for (let i = 1; i < parts.length; i += 2) {
      const title = parts[i];
      const text = (parts[i + 1] || '').trim();
      chapters.push({ title, content: text });
    }
    return chapters;
  };

  const chapters = story ? parseChapters(story.content) : [];
  const hasChapters = chapters.length > 1 || (chapters.length === 1 && chapters[0].title !== null);

  const [blindJudgingBlocked, setBlindJudgingBlocked] = useState(false);

  // Fetch story + comments (re-fetch comments when chapter changes)
  useEffect(() => {
    const fetchStory = async () => {
      try {
        const storyRes = await api.get(`/stories/${id}`);
        setStory(storyRes.data);
        if (user) {
          try {
            const bmRes = await api.get(`/bookmarks/check/${id}`);
            setBookmarked(bmRes.data.bookmarked);
          } catch (e) { console.error('Bookmark check failed:', e); }
        }
      } catch (error) {
        if (error.response?.status === 403 && error.response?.data?.detail?.includes('blind judging')) {
          setBlindJudgingBlocked(true);
        } else {
          navigate('/stories');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchStory();
  }, [id, api, navigate, user]);

  useEffect(() => {
    const fetchComments = async () => {
      try {
        const chapterParam = hasChapters ? `?chapter_index=${currentChapter}` : '';
        const res = await api.get(`/comments/${id}${chapterParam}`);
        setComments(res.data);
      } catch { console.error('Failed to fetch comments'); }
    };
    if (!loading) fetchComments();
  }, [id, api, currentChapter, hasChapters, loading]);

  // Scroll to specific comment from notification
  useEffect(() => {
    if (!loading && comments.length > 0 && location.hash) {
      const commentId = location.hash.replace('#comment-', '');
      if (commentId) {
        setTimeout(() => {
          const el = document.querySelector(`[data-testid="comment-${commentId}"], [data-testid="reply-${commentId}"]`);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.classList.add('ring-2', 'ring-brand-accent', 'ring-offset-2');
            setTimeout(() => el.classList.remove('ring-2', 'ring-brand-accent', 'ring-offset-2'), 3000);
          }
        }, 500);
      }
    }
  }, [loading, comments, location.hash]);

  // Reading progress
  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.scrollY / totalHeight) * 100;
      setReadingProgress(Math.min(progress, 100));
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLike = async () => {
    if (!user) { navigate('/login'); return; }
    try {
      const res = await api.post(`/stories/${id}/like`);
      setLiked(res.data.liked);
      setStory(prev => ({ ...prev, like_count: prev.like_count + (res.data.liked ? 1 : -1) }));
    } catch (error) { console.error('Like failed:', error); }
  };

  const handleBookmark = async () => {
    if (!user) { navigate('/login'); return; }
    try {
      const res = await api.post(`/bookmarks/${id}`);
      setBookmarked(res.data.bookmarked);
    } catch (error) { console.error('Bookmark failed:', error); }
  };

  const handlePublishFromPreview = async () => {
    if (!user || !story) return;
    const confirmed = window.confirm(
      'By publishing, you confirm this is your original writing — no AI-generated content.\n\nPublish now?'
    );
    if (!confirmed) return;
    setPublishing(true);
    try {
      await api.put(`/stories/${id}`, { ...story, is_draft: false });
      setStory(prev => ({ ...prev, is_draft: false }));
      const { toast } = await import('sonner');
      toast.success('Your work has been published!');
    } catch {
      const { toast } = await import('sonner');
      toast.error('Could not publish. Please try again.');
    } finally {
      setPublishing(false);
    }
  };

  const isAuthor = user && story && user.id === story.author_id;

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12">
        <Skeleton className="h-8 w-32 mb-8" />
        <Skeleton className="h-12 w-3/4 mb-4" />
        <Skeleton className="h-6 w-1/2 mb-8" />
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-6 w-full" />)}
        </div>
      </div>
    );
  }

  if (blindJudgingBlocked) {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="blind-judging-blocked">
        <Trophy className="w-16 h-16 text-brand-accent mx-auto mb-4" />
        <h1 className="font-serif text-2xl font-semibold text-text-primary mb-3">Entry Hidden During Judging</h1>
        <p className="text-text-secondary mb-6">
          This piece is part of an active challenge and is hidden to keep judging fair and blind.
          It will be publicly available once the results are announced.
        </p>
        <Link to="/challenges" className="text-brand-accent hover:underline font-medium">
          View Challenges
        </Link>
      </div>
    );
  }

  if (!story) return null;

  return (
    <div data-testid="story-reader-page">
      {/* Reading Progress Bar */}
      <div 
        className="reading-progress" 
        style={{ width: `${readingProgress}%` }}
        data-testid="reading-progress"
      />

      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Author Draft Bar — Edit / Publish from Preview */}
        {isAuthor && story.is_draft && (
          <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl px-5 py-3 mb-6" data-testid="draft-author-bar">
            <div className="flex items-center gap-2 text-amber-700 text-sm font-medium">
              <Eye className="w-4 h-4" />
              You're previewing your draft
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => navigate(`/story/${id}/edit`)}
                data-testid="draft-edit-btn"
              >
                <PenLine className="w-4 h-4" />
                Edit
              </Button>
              <Button
                size="sm"
                className="gap-2 bg-green-600 hover:bg-green-700 text-white"
                onClick={handlePublishFromPreview}
                disabled={publishing}
                data-testid="draft-publish-btn"
              >
                {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Publish
              </Button>
            </div>
          </div>
        )}

        {/* Back Button */}
        <Link 
          to="/stories" 
          className="inline-flex items-center gap-2 text-text-muted hover:text-text-primary mb-8 transition-colors"
          data-testid="back-to-stories"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Stories
        </Link>

        {/* Story Header */}
        <header className="mb-12">
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <span className="text-sm px-3 py-1 rounded-full bg-brand-primary/10 text-brand-primary">
              {story.genre}
            </span>
            {story.challenge_id && (
              <span className="text-sm px-3 py-1 rounded-full bg-brand-accent/10 text-brand-accent">
                Challenge Entry
              </span>
            )}
            {hasChapters && (
              <span className="text-sm px-3 py-1 rounded-full bg-blue-50 text-blue-600 font-medium inline-flex items-center gap-1">
                <BookOpen className="w-3.5 h-3.5" />
                {chapters.length} chapters
              </span>
            )}
            {story.awards?.length > 0 && story.awards.map((award) => (
              <span 
                key={`award-${award}`}
                className="text-sm px-3 py-1 rounded-full bg-brand-accent/15 text-brand-accent font-medium inline-flex items-center gap-1"
                data-testid={`story-award-badge-${award}`}
              >
                <Trophy className="w-3.5 h-3.5" />
                {award}
              </span>
            ))}
          </div>

          <h1 className="font-serif text-4xl lg:text-5xl font-semibold text-text-primary mb-6 leading-tight">
            {story.title}
          </h1>

          {story.summary && (
            <p className="text-xl text-text-secondary mb-6 font-reading">
              {story.summary}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-6 text-sm text-text-muted">
            <Link 
              to={`/writer/${story.author_id}`}
              className="flex items-center gap-2 hover:text-brand-accent transition-colors"
            >
              <User className="w-4 h-4" />
              {story.author_name}
            </Link>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {new Date(story.created_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </span>
            <span className="flex items-center gap-1">
              <Eye className="w-4 h-4" />
              {story.view_count} views
            </span>
          </div>

          {story.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {story.tags.map(tag => (
                <Link
                  key={tag}
                  to={`/stories?search=${tag}`}
                  className="text-sm text-text-muted hover:text-brand-accent"
                >
                  #{tag}
                </Link>
              ))}
            </div>
          )}
        </header>

        {/* Cover Image */}
        {story.cover_image && (
          <div className="mb-12 rounded-2xl overflow-hidden">
            <img 
              src={story.cover_image.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${story.cover_image}` : story.cover_image}
              alt={story.title}
              className="w-full h-64 sm:h-80 object-cover"
              style={{ objectPosition: `center ${story.cover_image_position || 50}%` }}
              data-testid="story-cover-image"
            />
          </div>
        )}

        {/* Table of Contents (for chaptered works) */}
        {hasChapters && (
          <div className="mb-8" data-testid="chapter-toc">
            <button
              onClick={() => setShowToc(!showToc)}
              className="flex items-center gap-2 text-text-primary font-semibold mb-3 hover:text-brand-accent transition-colors"
              data-testid="toggle-toc"
            >
              <List className="w-5 h-5" />
              Table of Contents ({chapters.length} chapters)
              {showToc ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {showToc && (
              <div className="bg-white rounded-xl border border-brand-primary/10 p-4">
                <ol className="space-y-1">
                  {chapters.map((ch, idx) => (
                    <li key={`ch-${ch.title}-${idx}`}>
                      <button
                        onClick={() => { setCurrentChapter(idx); setShowToc(false); }}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                          currentChapter === idx 
                            ? 'bg-brand-primary/10 text-brand-primary font-medium' 
                            : 'text-text-secondary hover:bg-surface-alt'
                        }`}
                        data-testid={`toc-chapter-${idx}`}
                      >
                        {idx + 1}. {ch.title}
                      </button>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        )}

        {/* Chapter Title */}
        {hasChapters && chapters[currentChapter]?.title && (
          <div className="mb-8 pb-4 border-b border-brand-primary/10" data-testid="chapter-title">
            <p className="text-sm text-text-muted mb-1">Chapter {currentChapter + 1} of {chapters.length}</p>
            <h2 className="font-serif text-2xl font-semibold text-text-primary">
              {chapters[currentChapter].title}
            </h2>
          </div>
        )}

        {/* Story Content */}
        <article 
          className="reading-content mb-8"
          style={{ 
            fontFamily: story.font_family || 'Lora',
            fontSize: story.font_size || '18px'
          }}
          data-testid="story-content"
        >
          <div 
            className="prose prose-lg max-w-none"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(
              (() => {
                let text = hasChapters ? chapters[currentChapter]?.content || '' : story.content;
                // Empty-chapter fallback: detect content with no real text after stripping HTML
                const strippedForEmptyCheck = text.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim();
                if (!strippedForEmptyCheck) {
                  return '<p style="color:#999;font-style:italic;text-align:center;padding:2rem 0;" data-testid="empty-chapter-fallback">This chapter has no content yet.</p>';
                }
                // Handle [image:url] tags
                text = text.replace(/\[image:(.*?)\]/g, (match, url) => {
                  const src = url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
                  return `<img src="${src}" alt="Story image" style="max-width:100%;border-radius:8px;margin:16px 0;" />`;
                });
                // Only add <br/> for plain text content (not HTML from rich editor)
                const isHtml = text.trimStart().startsWith('<');
                if (!isHtml) {
                  text = text.replace(/\n/g, '<br/>');
                }
                return text;
              })(),
              { ADD_TAGS: ['img', 'h1', 'h2', 'h3', 'u', 'hr', 'blockquote', 'ol', 'ul', 'li'], ADD_ATTR: ['src', 'alt', 'style'] }
            ) }}
          />
        </article>

        {/* Chapter Navigation */}
        {hasChapters && (
          <div className="flex items-center justify-between py-4 mb-8 border-t border-b border-brand-primary/10" data-testid="chapter-nav">
            <Button
              variant="outline"
              onClick={() => { setCurrentChapter(prev => prev - 1); window.scrollTo(0, 0); }}
              disabled={currentChapter === 0}
              className="gap-2"
              data-testid="prev-chapter-btn"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </Button>
            <span className="text-sm text-text-muted">
              {currentChapter + 1} / {chapters.length}
            </span>
            <Button
              variant="outline"
              onClick={() => { setCurrentChapter(prev => prev + 1); window.scrollTo(0, 0); }}
              disabled={currentChapter === chapters.length - 1}
              className="gap-2"
              data-testid="next-chapter-btn"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}

        {/* Action Bar */}
        <div className="flex items-center justify-between py-6 border-t border-b border-brand-primary/10 mb-12">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={handleLike}
              className={`gap-2 ${liked ? 'text-brand-accent' : 'text-text-muted'}`}
              data-testid="like-button"
            >
              <Heart className={`w-5 h-5 ${liked ? 'fill-current' : ''}`} />
              {story.like_count}
            </Button>
            <Button
              variant="ghost"
              onClick={() => setShowComments(!showComments)}
              className="gap-2 text-text-muted"
            >
              <MessageCircle className="w-5 h-5" />
              {comments.length} Reviews
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={handleBookmark}
              className={bookmarked ? 'text-brand-accent' : 'text-text-muted'}
              data-testid="bookmark-button"
              title={bookmarked ? 'Remove from reading list' : 'Add to reading list'}
            >
              <Bookmark className={`w-5 h-5 ${bookmarked ? 'fill-current' : ''}`} />
            </Button>
            <ShareMenu
              title={story.title}
              text={`Check out "${story.title}" by ${story.author_name} on Collectively Write`}
              url={window.location.href}
            />
            <Button variant="ghost" size="icon" className="text-text-muted">
              <Flag className="w-5 h-5" />
            </Button>
            {user && (story.author_id === user.id || user.role === 'admin') && (
              <ExportMenu storyId={id} />
            )}
          </div>
        </div>

        {/* Reviews Section */}
        <CommentSection
          comments={comments}
          storyId={id}
          chapterIndex={hasChapters ? currentChapter : null}
          chapterTitle={hasChapters ? chapters[currentChapter]?.title : null}
          onCommentAdded={(newComment, isEdit) => {
            if (isEdit) {
              setComments(prev => prev.map(c => c.id === newComment.id ? newComment : c));
            } else {
              setComments(prev => [...prev, newComment]);
            }
          }}
          onCommentDeleted={(commentId) => setComments(prev => prev.filter(c => c.id !== commentId && c.parent_id !== commentId))}
        />
      </div>
    </div>
  );
};

export default StoryReaderPage;
